import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Loader2, MessageSquare, Search, Users, Code, TrendingUp, Headphones, Clock, Shield, Zap, Calculator, Star, ChevronRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import CaseStudies from "@/components/CaseStudies";
import { SECTIONS } from "@/config/siteSections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForStartupsPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    company_website: "", funding_stage: "", company_size: "",
    role_title: "", role_department: "", role_seniority: "",
    location_preference: "", salary_range: "", urgency: "", additional_details: "",
    referred_by_id: ""
  });
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) setFormData((f) => ({ ...f, referred_by_id: ref }));
    axios.get(`${API}/referral/partners`).then((res) => setPartners(res.data || [])).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/client-inquiries`, formData);
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch within 24 hours.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Talent Advisory",
    "provider": {
      "@type": "Organization",
      "name": "Magnus Kor Talent Partners",
      "url": "https://magnuskor.com"
    },
    "areaServed": ["United Kingdom", "United States", "Germany", "Singapore", "India", "Canada", "Australia"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Talent Advisory Services",
      "itemListElement": [
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Retained Search", "description": "Dedicated advisory for VP, C-suite, and founding team hires at VC-backed SaaS startups. 20-25% of first-year CTC."}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Contingency Hiring", "description": "Pay-on-success hiring for IC to mid-level roles. 15-20% fee only on successful hire."}},
        {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Embedded Talent Partner", "description": "On-demand consultant embedded in your team for 3-6 month high-volume hiring sprints."}}
      ]
    }
  };

  const faqItems = [
    { q: "What types of startups does Magnus Kor work with?", a: "We work exclusively with VC-backed SaaS startups from Seed to Series C. Our sweet spot is companies scaling from 10 to 200+ employees that need to build Engineering, Product, Growth, or Customer Success teams." },
    { q: "How does the Retained Search model work?", a: "Retained Search is our dedicated advisory for senior and leadership hires (VP, C-suite, founding team). You pay a portion upfront and the remainder on successful hire. Typical fee is 20-25% of first-year CTC." },
    { q: "What is the Contingency model?", a: "Contingency is pay-on-success — no upfront cost. We source candidates for IC to mid-level roles and you only pay (15-20% of CTC) when a candidate is hired." },
    { q: "How quickly can you find candidates?", a: "For contingency roles, expect a qualified shortlist within 2-3 weeks. For retained executive searches, the process typically takes 4-8 weeks depending on the seniority and specialization required." },
    { q: "Do you offer a hire guarantee?", a: "Yes. Retained searches include a replacement guarantee — if a hire doesn't work out within the guarantee period, we restart the search at no additional cost." },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": { "@type": "Answer", "text": item.a }
    }))
  };

  return (
    <div className="min-h-screen pt-24 bg-[#FAF9F6]">
      <SEO 
        title="For Startups - Build Your Core Team"
        description="We help VC-backed SaaS startups hire Engineering, Product, Growth, and Customer Success teams. Retained search, contingency hiring, and embedded talent advisory that scales with you."
        keywords="startup hiring partner, SaaS team building, engineering team hiring, product team recruitment, Series A talent strategy, Series B hiring plan, startup CTO search, VP Engineering hiring, growth team building, embedded recruiter SaaS, startup recruitment agency 2026"
        canonical="https://magnuskor.com/for-startups"
        jsonLd={[serviceSchema, faqSchema]}
      />
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="max-w-3xl">
            <span className="text-overline">For Startups</span>
            <h1 className="heading-hero mt-4 mb-6">
              Build the teams that drive growth
            </h1>
            <p className="text-body-lg mb-8">
              We partner with VC-backed SaaS startups to hire the core teams that scale your business—from your first product hire to your leadership bench. Talent advisory for every stage of growth.
            </p>
            <a href="#contact-form" className="btn-luxury btn-luxury-dark">
              Start a Conversation
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Trust Badges — based on real, published promises */}
      <section className="border-y border-gray-200 bg-[#FAF9F6]" data-testid="trust-badges">
        <div className="max-w-7xl mx-auto px-8 md:px-16 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-4 gap-x-8">
            {[
              { icon: Shield, label: "90-day replacement guarantee" },
              { icon: Star, label: "Transparent, published pricing" },
              { icon: Zap, label: "Pay-on-success options" },
              { icon: Users, label: "Founder-led searches" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3" data-testid={`trust-badge-${label.split(' ')[0].toLowerCase()}`}>
                <span className="w-9 h-9 flex-shrink-0 border border-[#B8860B]/30 flex items-center justify-center">
                  <Icon size={16} className="text-[#B8860B]" strokeWidth={1.75} />
                </span>
                <span className="text-sm text-gray-700">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE PLACE - New Section */}
      <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="max-w-2xl mb-16">
            <span className="text-[#B8860B] text-xs tracking-[0.25em] uppercase">What We Place</span>
            <h2 className="text-white heading-section mt-4 mb-6">
              The roles that define your next stage
            </h2>
            <p className="text-gray-400 text-lg">
              We focus on the functions that determine whether a Series A startup becomes a Series B one. From individual contributors to leadership—we scale with you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Code,
                title: "Engineering",
                roles: ["Software Engineers", "Senior Engineers", "Tech Leads", "Engineering Managers", "VPs & CTOs"],
                desc: "The builders who ship your product"
              },
              {
                icon: TrendingUp,
                title: "Product & Growth",
                roles: ["Product Managers", "Senior PMs", "Growth Marketers", "Demand Gen", "Product Leaders"],
                desc: "The minds behind what you build and how it spreads"
              },
              {
                icon: Users,
                title: "Go-to-Market",
                roles: ["SDRs & AEs", "Account Executives", "Sales Managers", "Marketing Managers", "Revenue Leaders"],
                desc: "The team that turns product into pipeline"
              },
              {
                icon: Headphones,
                title: "Customer Success",
                roles: ["CS Representatives", "CS Managers", "Implementation Specialists", "CS Directors", "CS Leadership"],
                desc: "The people who turn users into advocates"
              }
            ].map((category, i) => (
              <div key={i} className="group p-8 border border-gray-800 hover:border-[#B8860B]/50 transition-colors">
                <category.icon size={28} className="text-[#B8860B] mb-6" strokeWidth={1.5} />
                <h3 className="text-white font-display text-xl mb-2">{category.title}</h3>
                <p className="text-gray-500 text-sm mb-6">{category.desc}</p>
                <ul className="space-y-2">
                  {category.roles.map((role, j) => (
                    <li key={j} className="text-gray-400 text-sm flex items-center gap-2">
                      <span className="w-1 h-1 bg-[#B8860B] rounded-full"></span>
                      {role}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-500">From your first IC hire to building out leadership—we scale with you.</p>
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-overline">How We Work</span>
              <h2 className="heading-section mt-4 mb-6">Not the usual recruiter playbook</h2>
              <p className="text-body-lg mb-8">
                Most recruiters optimize for volume. We optimize for staying power. A hire that leaves at 6 months costs you more than an empty seat.
              </p>
              
              <div className="space-y-6">
                {[
                  { num: "01", title: "We learn your business first", desc: "Deep-dive into your product, roadmap, and culture—not a 15-minute intake call." },
                  { num: "02", title: "Small, curated shortlists", desc: "3–5 candidates, not 30. Every one screened for skill, stage-fit, and motivation." },
                  { num: "03", title: "We stay through the first 90 days", desc: "Placement isn't the finish line. We check in through onboarding." }
                ].map((step, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="text-[#B8860B] font-display text-2xl">{step.num}</span>
                    <div>
                      <h4 className="font-display text-lg mb-1">{step.title}</h4>
                      <p className="text-body text-sm">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img loading="lazy"
                src="/images/site/pexels-7495609-4d7a2e.jpg"
                alt="Magnus Kor consultant discussing hiring strategy with startup founders"
                className="w-full img-desaturate hover:filter-none transition-all duration-500"
              />
              <div className="absolute -bottom-6 -right-6 w-full h-full border border-[#B8860B]/30 -z-10"></div>
            </div>
          </div>

          {/* Typical Engagement Timeline */}
          <div className="mt-20 pt-16 border-t border-gray-200">
            <h3 className="text-center font-display text-xl mb-12">Typical Engagement Timeline</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { week: "Week 1", phase: "Discovery", desc: "Deep-dive into your business, culture, and what success looks like" },
                { week: "Week 2-3", phase: "Sourcing", desc: "Targeted outreach to qualified candidates from our network" },
                { week: "Week 3-4", phase: "Shortlist", desc: "Present 3-5 vetted candidates with detailed assessments" },
                { week: "Ongoing", phase: "Support", desc: "Interview coordination, offer negotiation, and onboarding check-ins" }
              ].map((item, i) => (
                <div key={i} className="relative">
                  <div className="bg-white p-6 h-full">
                    <p className="text-[#B8860B] text-xs font-medium tracking-wider uppercase mb-2">{item.week}</p>
                    <h4 className="font-display text-lg mb-2">{item.phase}</h4>
                    <p className="text-body text-sm">{item.desc}</p>
                  </div>
                  {i < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-px bg-[#B8860B]/30"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What We Believe */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="text-overline">Our Philosophy</span>
              <h2 className="heading-section mt-4 mb-8">What makes us different</h2>
              
              <div className="space-y-6">
                {[
                  {
                    title: "Talent advisory, not just executives",
                    desc: "We help you build entire teams—from your first software engineer to your VP of Engineering. One partner for every stage of growth."
                  },
                  {
                    title: "We'd rather say no than do it wrong",
                    desc: "If we're not the right fit for your search, we'll tell you. If your timeline doesn't allow for quality work, we'll be honest about that too."
                  },
                  {
                    title: "We believe in relationships over transactions",
                    desc: "We're not trying to maximize placements. We're trying to build long-term partnerships with founders who value quality and honesty."
                  }
                ].map((item, i) => (
                  <div key={i} className="border-l-2 border-[#B8860B] pl-6">
                    <h4 className="font-display text-lg mb-2">{item.title}</h4>
                    <p className="text-body text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#1A1A1A] p-10 md:p-12 text-white">
              <span className="text-[#B8860B] text-xs tracking-[0.2em] uppercase">Our Commitments</span>
              <h3 className="font-display text-2xl mt-4 mb-8">What we guarantee</h3>
              
              <div className="space-y-6">
                {[
                  "We'll never send you unqualified candidates to hit a quota",
                  "We'll be transparent about our process and timeline",
                  "We'll tell you honestly if we think something won't work",
                  "We'll prioritize fit over speed—every time",
                  "If a hire doesn't work out in 90 days, we re-run the search—on us"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={18} className="text-[#B8860B] mt-1 flex-shrink-0" />
                    <p className="text-gray-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Anonymised Placement Outcomes */}
      {SECTIONS.forStartupsCaseStudies && <CaseStudies variant="light" />}

      {/* How We Engage - Pricing Models */}
      <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="text-center mb-16">
            <span className="text-[#B8860B] text-xs tracking-[0.25em] uppercase">How We Engage</span>
            <h2 className="text-white heading-section mt-4 mb-4">Choose the model that fits your needs</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Transparent pricing, flexible engagement. Whether you need one urgent hire or ongoing recruitment support, we have a model that works.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Priority Search */}
            <div className="bg-white p-8 relative">
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#B8860B]"></div>
              <div className="flex items-center gap-2 mb-4">
                <Zap size={20} className="text-[#B8860B]" />
                <span className="text-xs text-[#B8860B] font-medium tracking-wider uppercase">Fastest</span>
              </div>
              <h3 className="font-display text-2xl mb-2">Priority Search</h3>
              <p className="text-gray-500 text-sm mb-6">For urgent, business-critical roles</p>
              
              <div className="mb-6">
                <p className="text-3xl font-display">25%</p>
                <p className="text-sm text-gray-500">of first-year CTC</p>
              </div>

              <div className="border-t border-gray-100 pt-6 mb-6">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Fee Structure</p>
                <div className="space-y-2 text-sm">
                  <p><span className="font-medium">5% upfront</span> — Engagement fee to start</p>
                  <p><span className="font-medium">20% on placement</span> — Success fee on hire</p>
                </div>
                <div className="mt-3 p-3 bg-green-50 text-xs text-green-700 rounded">
                  <p><strong>Refund Policy:</strong> 100% refund within 10 business days. After 10 days, 1% service charge retained.</p>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6 mb-6">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">What's Included</p>
                <ul className="space-y-2">
                  {[
                    "First shortlist in ~10 business days",
                    "Dedicated search team assigned",
                    "3-5 pre-vetted candidates",
                    "Full interview coordination",
                    "Offer negotiation support",
                    "Weekly progress updates",
                    "90-day replacement guarantee"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check size={16} className="text-[#B8860B] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Best For</p>
                <p className="text-sm text-gray-600">Leadership hires, roles blocking milestones, time-sensitive positions</p>
              </div>
            </div>

            {/* Partnership Search */}
            <div className="bg-white p-8 relative">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gray-300"></div>
              <div className="flex items-center gap-2 mb-4">
                <Shield size={20} className="text-gray-400" />
                <span className="text-xs text-gray-500 font-medium tracking-wider uppercase">No Risk</span>
              </div>
              <h3 className="font-display text-2xl mb-2">Partnership Search</h3>
              <p className="text-gray-500 text-sm mb-6">For important hires without urgency</p>
              
              <div className="mb-6">
                <p className="text-3xl font-display">20%</p>
                <p className="text-sm text-gray-500">of first-year CTC</p>
              </div>

              <div className="border-t border-gray-100 pt-6 mb-6">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Fee Structure</p>
                <div className="space-y-2 text-sm">
                  <p><span className="font-medium">$0 upfront</span> — No commitment to start</p>
                  <p><span className="font-medium">100% on placement</span> — Pay only when you hire</p>
                </div>
                <div className="mt-3 p-3 bg-blue-50 text-xs text-blue-700 rounded">
                  <p><strong>Zero Risk:</strong> You only pay when we successfully place a candidate you love.</p>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6 mb-6">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">What's Included</p>
                <ul className="space-y-2">
                  {[
                    "First shortlist in ~2-3 weeks",
                    "Thorough discovery & intake",
                    "3-5 screened candidates",
                    "Interview coordination",
                    "Offer guidance & support",
                    "Bi-weekly updates",
                    "60-day replacement guarantee"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check size={16} className="text-[#B8860B] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Best For</p>
                <p className="text-sm text-gray-600">Standard hires, building team steadily, budget-conscious startups</p>
              </div>
            </div>

            {/* Growth Partner */}
            <div className="bg-[#FAF9F6] p-8 relative border-2 border-[#B8860B]">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#B8860B] text-white text-xs px-3 py-1 font-medium">
                BEST VALUE
              </div>
              <div className="flex items-center gap-2 mb-4">
                <Star size={20} className="text-[#B8860B]" />
                <span className="text-xs text-[#B8860B] font-medium tracking-wider uppercase">Recommended</span>
              </div>
              <h3 className="font-display text-2xl mb-2">Growth Partner</h3>
              <p className="text-gray-500 text-sm mb-6">For startups with ongoing hiring needs</p>
              
              <div className="mb-6">
                <p className="text-3xl font-display">$500<span className="text-lg text-gray-500">/mo</span> + 15%</p>
                <p className="text-sm text-gray-500">monthly retainer + success fee</p>
              </div>

              <div className="border-t border-gray-200 pt-6 mb-6">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Fee Structure</p>
                <div className="space-y-2 text-sm">
                  <p><span className="font-medium">$500/month</span> — Ongoing partnership</p>
                  <p><span className="font-medium">15% on placement</span> — Reduced success fee</p>
                </div>
                <div className="mt-3 p-3 bg-[#B8860B]/10 text-xs text-[#B8860B] rounded">
                  <p><strong>Save 32%+</strong> compared to Priority Search when hiring multiple roles.</p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 mb-6">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">What's Included</p>
                <ul className="space-y-2">
                  {[
                    "Continuous talent sourcing",
                    "Unlimited role briefs",
                    "Monthly hiring strategy call",
                    "Priority access to candidates",
                    "Employer branding guidance",
                    "Dedicated account manager",
                    "90-day replacement guarantee"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check size={16} className="text-[#B8860B] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Best For</p>
                <p className="text-sm text-gray-600">Startups hiring 3+ roles, scaling teams, Series A-C companies</p>
              </div>
            </div>
          </div>

          {/* Comparison & Calculator CTA */}
          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-6">Not sure which model is right for you?</p>
            <Link to="/tools" className="btn-luxury btn-luxury-gold">
              <Calculator size={18} />
              Calculate Your Hiring Cost
            </Link>
          </div>

          {/* Disclaimer */}
          <div className="mt-12 mx-auto max-w-3xl p-5 border border-gray-700 bg-white/5 rounded">
            <p className="text-xs text-gray-400 leading-relaxed">
              <span className="text-[#B8860B] font-medium">Disclaimer:</span> The fee structures outlined above are indicative and may vary based on role complexity, seniority level, market conditions, and scope of engagement. All percentages are calculated on first-year CTC (Cost to Company) unless otherwise agreed. The 5% upfront engagement fee (Priority Search) is fully refundable within 10 business days; a 1% administrative charge applies after this period. Replacement guarantees and all terms are subject to a signed engagement agreement. For a personalised quote, <Link to="/contact" className="text-[#B8860B] underline hover:text-white transition-colors">get in touch</Link>.
            </p>
          </div>

          {/* Quick Comparison Table */}
          <div className="mt-16 bg-white/5 p-8 rounded">
            <h3 className="text-white font-display text-xl mb-6 text-center">Quick Comparison</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="text-left py-3 pr-4"></th>
                    <th className="text-center py-3 px-4">Priority</th>
                    <th className="text-center py-3 px-4">Partnership</th>
                    <th className="text-center py-3 px-4 text-[#B8860B]">Growth Partner</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-gray-800">
                    <td className="py-3 pr-4 text-gray-500">Total Fee</td>
                    <td className="text-center py-3 px-4">25%</td>
                    <td className="text-center py-3 px-4">20%</td>
                    <td className="text-center py-3 px-4 text-[#B8860B]">$500/mo + 15%</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 pr-4 text-gray-500">Time to Shortlist</td>
                    <td className="text-center py-3 px-4">~10 days</td>
                    <td className="text-center py-3 px-4">~2-3 weeks</td>
                    <td className="text-center py-3 px-4 text-[#B8860B]">Ongoing</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 pr-4 text-gray-500">Upfront Cost</td>
                    <td className="text-center py-3 px-4">5% (refundable)</td>
                    <td className="text-center py-3 px-4">None</td>
                    <td className="text-center py-3 px-4 text-[#B8860B]">$500/month</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 pr-4 text-gray-500">Guarantee</td>
                    <td className="text-center py-3 px-4">90 days</td>
                    <td className="text-center py-3 px-4">60 days</td>
                    <td className="text-center py-3 px-4 text-[#B8860B]">90 days</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 text-gray-500">Best For</td>
                    <td className="text-center py-3 px-4">Urgent roles</td>
                    <td className="text-center py-3 px-4">Single hires</td>
                    <td className="text-center py-3 px-4 text-[#B8860B]">3+ hires</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#FAF9F6]" data-testid="startup-faq">
        <div className="max-w-3xl mx-auto px-8 md:px-16">
          <span className="text-overline">Common Questions</span>
          <h2 className="heading-section mt-4 mb-12">Frequently asked questions</h2>
          <div className="space-y-6">
            {faqItems.map((item, i) => (
              <details key={i} className="group border-b border-gray-200 pb-6">
                <summary className="flex justify-between items-center cursor-pointer text-gray-900 font-medium text-lg">
                  <h3 className="pr-4">{item.q}</h3>
                  <ChevronRight size={18} className="text-[#B8860B] group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link to="/tools" className="group p-6 border border-gray-800 hover:border-[#B8860B]/50 transition-colors">
              <h3 className="text-white font-display text-lg mb-2 group-hover:text-[#B8860B] transition-colors">Free Hiring Tools</h3>
              <p className="text-gray-500 text-sm">Salary benchmarks, equity calculator, and hiring readiness checklist.</p>
            </Link>
            <Link to="/jobs" className="group p-6 border border-gray-800 hover:border-[#B8860B]/50 transition-colors">
              <h3 className="text-white font-display text-lg mb-2 group-hover:text-[#B8860B] transition-colors">Open Roles</h3>
              <p className="text-gray-500 text-sm">Browse curated positions at VC-backed SaaS companies.</p>
            </Link>
            <Link to="/insights" className="group p-6 border border-gray-800 hover:border-[#B8860B]/50 transition-colors">
              <h3 className="text-white font-display text-lg mb-2 group-hover:text-[#B8860B] transition-colors">Hiring Insights</h3>
              <p className="text-gray-500 text-sm">Expert articles on startup hiring, compensation, and team building.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-8 md:px-16">
          <div className="text-center mb-12">
            <span className="text-overline">Get Started</span>
            <h2 className="heading-section mt-4 mb-4">Tell us about your hiring needs</h2>
            <p className="text-body">
              No commitment required. Let's start with a conversation about what you're looking for.
            </p>
          </div>

          {submitted ? (
            <div className="bg-[#FAF9F6] p-12 text-center">
              <Check size={48} className="text-[#B8860B] mx-auto mb-4" />
              <h3 className="font-display text-2xl mb-2">Thank you</h3>
              <p className="text-body">We'll be in touch within 24 hours to schedule a conversation.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="client-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Company Name *</label>
                  <input type="text" required value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} className="input-luxury border-b border-gray-200" data-testid="client-company" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Your Name *</label>
                  <input type="text" required value={formData.contact_name} onChange={e => setFormData({...formData, contact_name: e.target.value})} className="input-luxury border-b border-gray-200" data-testid="client-name" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Email *</label>
                  <input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="input-luxury border-b border-gray-200" data-testid="client-email" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Funding Stage</label>
                  <select value={formData.funding_stage} onChange={e => setFormData({...formData, funding_stage: e.target.value})} className="input-luxury border-b border-gray-200 bg-transparent">
                    <option value="">Select stage</option>
                    <option value="Seed">Seed</option>
                    <option value="Series A">Series A</option>
                    <option value="Series B">Series B</option>
                    <option value="Series C+">Series C+</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 block mb-2">Referred by a Magnus Kor partner (optional)</label>
                <select value={formData.referred_by_id} onChange={e => setFormData({...formData, referred_by_id: e.target.value})} className="input-luxury border-b border-gray-200 bg-transparent" data-testid="client-referrer">
                  <option value="">Found you directly / not referred</option>
                  {partners.map(p => (
                    <option key={p.uid} value={p.uid}>{p.name} — {p.role_label}</option>
                  ))}
                </select>
                {formData.referred_by_id && !partners.some(p => p.uid === formData.referred_by_id) && (
                  <p className="text-xs text-[#B8860B] mt-2" data-testid="referrer-confirm">✓ You were referred by a Magnus Kor partner</p>
                )}
              </div>

              <div className="pt-4">
                <h4 className="font-display text-lg mb-6">About the Role</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm text-gray-600 block mb-2">Role Title *</label>
                    <input type="text" required value={formData.role_title} onChange={e => setFormData({...formData, role_title: e.target.value})} className="input-luxury border-b border-gray-200" placeholder="e.g., VP of Engineering" data-testid="client-role" />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-2">Department *</label>
                    <select required value={formData.role_department} onChange={e => setFormData({...formData, role_department: e.target.value})} className="input-luxury border-b border-gray-200 bg-transparent">
                      <option value="">Select department</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Product">Product</option>
                      <option value="Sales">Sales</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Customer Success">Customer Success</option>
                      <option value="Operations">Operations</option>
                      <option value="People">People/HR</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-2">Seniority *</label>
                    <select required value={formData.role_seniority} onChange={e => setFormData({...formData, role_seniority: e.target.value})} className="input-luxury border-b border-gray-200 bg-transparent">
                      <option value="">Select level</option>
                      <option value="IC">Individual Contributor</option>
                      <option value="Senior">Senior IC</option>
                      <option value="Manager">Manager / Lead</option>
                      <option value="Director">Director</option>
                      <option value="VP">VP</option>
                      <option value="C-level">C-level</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 block mb-2">Timeline</label>
                    <select value={formData.urgency} onChange={e => setFormData({...formData, urgency: e.target.value})} className="input-luxury border-b border-gray-200 bg-transparent">
                      <option value="">Select timeline</option>
                      <option value="ASAP">ASAP</option>
                      <option value="1-2 months">1-2 months</option>
                      <option value="3+ months">3+ months</option>
                      <option value="Exploratory">Just exploring</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 block mb-2">Anything else you'd like to share?</label>
                <textarea rows={4} value={formData.additional_details} onChange={e => setFormData({...formData, additional_details: e.target.value})} className="input-luxury border border-gray-200 p-4 resize-none w-full" placeholder="Tell us more about what you're looking for..." />
              </div>

              <button type="submit" disabled={submitting} className="btn-luxury btn-luxury-dark w-full" data-testid="client-submit">
                {submitting ? <Loader2 size={18} className="animate-spin" /> : "Submit Inquiry"}
              </button>

              <p className="text-xs text-gray-400 text-center">
                No commitment. We'll reach out to schedule a conversation.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ForStartupsPage;
